import React from 'react'
import PropTypes from 'prop-types'
import styled from '@emotion/styled'
import { stylesIf } from 'utils/styles'
import {
  ROUND_SHAPES_BY_DASH_AND_DETAIL,
  ROTATE_TEXT_BY_DETAIL,
} from 'components/SwitchAndGauge/definitions/dashConstants'
import {
  getModifiedLocationId,
  get42NPlugDisplayNumber,
} from 'components/SwitchAndGauge/utils'

import { useSelector, useMakeSelector } from 'hooks/redux'

import {
  makeGetLocationWidthAndHeight,
  makeGetComponentMeta,
  makeGetLocationDefinition,
  getDashboardType,
  getVehicleSubType,
} from 'selectors/dash'

const PartNumberView = ({ location, selected }) => {
  const dimensions = useMakeSelector(makeGetLocationWidthAndHeight, location)
  const { capPartNumber, panelNumber, panelDescription } = useMakeSelector(
    makeGetComponentMeta,
    location
  )
  const { detail } = useMakeSelector(makeGetLocationDefinition, location)
  const dashboardType = useSelector(getDashboardType)
  const vehicleSubtype = useSelector(getVehicleSubType)

  // Apply the 42N plug presentation override so the part-number overlay is
  // consistent with the dashboard graphic, tooltip, and drawer header. (#485445)
  const displayedPanelNumber = get42NPlugDisplayNumber(
    panelNumber,
    vehicleSubtype,
    panelDescription
  )

  const modifiedLocationId = getModifiedLocationId(
    location,
    displayedPanelNumber,
    vehicleSubtype
  )

  return (
    <PartNumberOverlay
      dimensions={dimensions}
      customSwitch={capPartNumber}
      dashboardType={dashboardType}
      detail={detail}
    >
      <span>{modifiedLocationId}</span>
      <span>{displayedPanelNumber}</span>
      {capPartNumber && (
        <>
          <span>{capPartNumber}</span>
        </>
      )}
    </PartNumberOverlay>
  )
}

PartNumberView.propTypes = {
  location: PropTypes.string,
  selected: PropTypes.bool,
}

export default PartNumberView

const PartNumberOverlay = styled.div((props) => {
  const {
    dimensions: { width, height },
    selected,
    customSwitch,
    dashboardType,
    detail,
  } = props

  const roundShapesList = ROUND_SHAPES_BY_DASH_AND_DETAIL[dashboardType] || []

  const {
    theme: {
      sAndGPartNumberBackgroundColor,
      sAndGPartNumberBorderColor,
      sAndGPartNumberBackgroundColorSelected,
      sAndGPartNumberColorSelected,
    },
  } = props

  const fontColor = selected ? sAndGPartNumberColorSelected : 'default'
  const lineHeight = customSwitch ? '1' : '1.2'
  const borderRadius = roundShapesList.includes(detail) ? '50%' : '0'
  const addRotate = ROTATE_TEXT_BY_DETAIL.includes(detail) && height > width

  const defaultStyles = `
    background: ${
      selected
        ? sAndGPartNumberBackgroundColorSelected
        : sAndGPartNumberBackgroundColor
    };
    position: absolute;
    border: solid 3px;
    line-height: ${lineHeight};
    ${stylesIf(selected, `border-color: ${sAndGPartNumberBorderColor};`)}
    cursor: pointer;

    &:hover {
      border-color: ${sAndGPartNumberBorderColor};
    }

    span {
      color: ${fontColor};
      font-weight: bold;
      display: inline-block;
      font-size: 12px;
      letter-space: .5px;
      font-smoothing: antialiased;
    }

    span:last-of-type {
      word-break: break-all;
      text-align: center;
    }
  `
  return `
    width: ${addRotate ? height : width}px;
    height: ${addRotate ? width : height}px;
    border-radius: ${borderRadius};
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    ${stylesIf(addRotate, 'transform: translate(-25%, 50%) rotate(-90deg);')}
    ${defaultStyles}
    ${stylesIf(
      addRotate,
      `
      svg[class*="StyledCustomGraphicIcon"] {
        position: absolute !important;
      }
    `
    )}
  `
})
